import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { Merge } from 'type-fest';

import { TimezoneAbbreviationEnum } from '_lib/api/apiTypes.shim';

import { abbreviatedTimezoneToIana } from '../../utils/date.utils';

export class DatePickerAdapter extends AdapterLuxon {}

/**
 * Converts a JS date to a Luxon DateTime object.
 */
const fromJSDate = (date: Date, timezone?: TimezoneAbbreviationEnum): DateTime => {
  if (!timezone) return DateTime.fromJSDate(date);
  return DateTime.fromJSDate(date).setZone(abbreviatedTimezoneToIana(timezone));
};

/**
 * Converts an ISO string to a Luxon DateTime object.
 */
const fromISOString = (isoString: string, timezone?: TimezoneAbbreviationEnum): DateTime => {
  if (!timezone) return DateTime.fromISO(isoString);
  return DateTime.fromISO(isoString, { zone: abbreviatedTimezoneToIana(timezone) });
};

/**
 * Converts a Luxon DateTime object to a JS date
 */
const toJsDate = (dateTime: DateTime, timezone?: TimezoneAbbreviationEnum): Date => {
  if (!timezone) return dateTime.toJSDate();
  return dateTime.setZone(abbreviatedTimezoneToIana(timezone)).toJSDate();
};

export type AdapterDate = DateTime;

type PickerValue = Date | string;

export type DatePickerCommonProps = {
  minDate?: PickerValue;
  minDateTime?: PickerValue;
  minTime?: PickerValue;
  maxDate?: PickerValue;
  maxDateTime?: PickerValue;
  maxTime?: PickerValue;
  value?: PickerValue | null;
  onChange?: (value: Date | null) => void;
  onMonthChange?: (value: Date | null) => void;
  timezone?: TimezoneAbbreviationEnum;
};

/**
 * Intended use is to override properties of MUI Date Picker Props.
 * Only properties that are in both (the MUI Date Picker prop type and our DatePickerCommonProps type) will be remapped
 * to the type of DatePickerCommonProps
 */
export type OverrideDatePickerCommonProps<T> = Merge<
  T,
  Pick<DatePickerCommonProps, keyof T & keyof DatePickerCommonProps>
>;

export const toAdapterDate = (dateOrDateString: Date | string, timezone?: TimezoneAbbreviationEnum): AdapterDate => {
  if (typeof dateOrDateString === 'string') {
    return fromISOString(dateOrDateString, timezone);
  } else {
    return fromJSDate(dateOrDateString, timezone);
  }
};

export const fromAdapterDate = (dateAdapter: AdapterDate): Date => toJsDate(dateAdapter);
